"use client";

import { useTranslations } from "next-intl";
import { ParticipantCardHeader } from "./ParticipantCardHeader";
import { ParticipantChampionshipCategorySection } from "./ParticipantChampionshipCategorySection";
import { ParticipantChampionshipPartner1Section } from "./ParticipantChampionshipPartner1Section";
import { ParticipantChampionshipPartner2Section } from "./ParticipantChampionshipPartner2Section";
import { ParticipantGuestBasicSection } from "./ParticipantGuestBasicSection";
import type { ParticipantFormData } from "./registrationFlowTypes";

export interface ParticipantCardProps {
    participant: ParticipantFormData;
    index: number;
    isValid: boolean;
    errors: Record<string, string>;
    onUpdate: (data: Partial<ParticipantFormData>) => void;
    onToggle: () => void;
    onRemove: () => void;
    canRemove: boolean;
    isPrimaryProfile: boolean;
    primaryProfileLocked: boolean;
    /** choose = pick owner; lockedOne = single card; cabinetOwnerBadge = logged-in owner (read-only); off = hidden */
    primaryProfileMode: "choose" | "lockedOne" | "cabinetOwnerBadge" | "off";
    onSetPrimary: () => void;
    /** Встроенная карточка в модалке: всегда развёрнута, без chevron / сворачивания по шапке */
    embedInModal?: boolean;
    hidePartner2Section?: boolean;
    /** Модалка ЛК/мероприятий: без блока категории чемпионата */
    hideChampionshipCategorySection?: boolean;
    lockParticipationModeToggle?: boolean;
    /**
     * Телефон основного участника (не partner2): true = необязателен (вторичные карточки),
     * false = обязателен (primary).
     */
    phoneOptional?: boolean;
    /** Модалка «Добавить участника»: телефон всегда необязателен (перекрывает phoneOptional). */
    modalNewParticipant?: boolean;
}

export function ParticipantCard({
    participant,
    index,
    isValid,
    errors,
    onUpdate,
    onToggle,
    onRemove,
    canRemove,
    isPrimaryProfile,
    primaryProfileLocked,
    primaryProfileMode,
    onSetPrimary,
    embedInModal = false,
    hidePartner2Section = false,
    hideChampionshipCategorySection = false,
    lockParticipationModeToggle = false,
    phoneOptional = false,
    modalNewParticipant = false,
}: ParticipantCardProps) {
    const t = useTranslations("strings.registrationFlow");
    const effectivePhoneOptional = modalNewParticipant ? true : phoneOptional;
    const hasName = participant.firstName && participant.lastName;
    const displayName = hasName
        ? `${participant.firstName} ${participant.lastName}`
        : participant.isChampionshipParticipant
          ? t("displayPair", { n: index + 1 })
          : t("displayParticipant", { n: index + 1 });

    const expanded = embedInModal || participant.isExpanded;

    return (
        <div
            className={`min-w-0 max-w-full rounded-xl border-2 bg-white shadow-lg transition-all ${
                expanded
                    ? "border-fest-gold"
                    : isValid
                      ? "border-fest-apple-green"
                      : "border-fest-border-warm"
            }`}
        >
            <ParticipantCardHeader
                displayName={displayName}
                participant={participant}
                isValid={isValid}
                canRemove={canRemove}
                isPrimaryProfile={isPrimaryProfile}
                primaryProfileLocked={primaryProfileLocked}
                primaryProfileMode={primaryProfileMode}
                onToggle={onToggle}
                onUpdate={onUpdate}
                onRemove={onRemove}
                onSetPrimary={onSetPrimary}
                lockParticipationModeToggle={lockParticipationModeToggle}
                hideExpandToggle={embedInModal}
                disableHeaderCollapseToggle={embedInModal}
            />

            {expanded && (
                <div className="animate-fade-in min-w-0 max-w-full space-y-3 overflow-x-clip px-3 pb-3 pt-0.5 sm:px-5">
                    {participant.isChampionshipParticipant && (
                        <ParticipantChampionshipPartner1Section
                            participant={participant}
                            index={index}
                            errors={errors}
                            onUpdate={onUpdate}
                            phoneOptional={effectivePhoneOptional}
                        />
                    )}

                    {!participant.isChampionshipParticipant && (
                        <ParticipantGuestBasicSection
                            participant={participant}
                            index={index}
                            errors={errors}
                            onUpdate={onUpdate}
                            phoneOptional={effectivePhoneOptional}
                        />
                    )}

                    {participant.isChampionshipParticipant && (
                        <>
                            {!hidePartner2Section && (
                                <ParticipantChampionshipPartner2Section
                                    participant={participant}
                                    index={index}
                                    errors={errors}
                                    onUpdate={onUpdate}
                                />
                            )}
                            {!hideChampionshipCategorySection && (
                                <ParticipantChampionshipCategorySection
                                    participant={participant}
                                    errors={errors}
                                    onUpdate={onUpdate}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
